import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { Loading } from '../../components';
import { Background } from './Loading.styles';

type LoadingEntry = {
    id: string;
    message: string;
};

type LoadingContextType = {
    isLoading: boolean;
    startLoading: (message?: string) => string;
    stopLoading: (id: string) => void;
};

const LoadingContext = createContext<LoadingContextType>({
    isLoading: false,
    startLoading: () => '',
    stopLoading: () => {}
});

const LoadingProvider = ({ children }: { children: ReactNode }) => {
    const [loadingQueue, setLoadingQueue] = useState<LoadingEntry[]>([]);

    const startLoading = useCallback((message = 'A carregar...') => {
        const id = crypto.randomUUID();
        setLoadingQueue((prev) => [...prev, { id, message }]);
        return id;
    }, []);

    const stopLoading = useCallback((id: string) => {
        setLoadingQueue((prev) => prev.filter((entry) => entry.id !== id));
    }, []);

    const currentMessage = loadingQueue.length > 0 ? loadingQueue[loadingQueue.length - 1].message : '';

    return (
        <LoadingContext.Provider value={{ isLoading: loadingQueue.length > 0, startLoading, stopLoading }}>
            {loadingQueue.length > 0 && (
                <Background>
                    <Loading title={currentMessage} />
                </Background>
            )}
            {children}
        </LoadingContext.Provider>
    );
};

export const useLoading = () => {
    const context = useContext(LoadingContext);
    if (!context) {
        throw new Error('useLoading must be used within a LoadingProvider');
    }
    return context;
};

export default LoadingProvider;
